/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useState } from 'react';
import { InstantCredit } from 'state/instantCredit';
import { Tenant } from 'state/tenant';

import { Grid, Box } from '@material-ui/core';

import useTranslate, {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import { useCachedAttribute } from 'features/ResetApplicationRoute';
import styles from './styles';
import useResponsiveQuery from 'hooks/useResponsiveQuery';
import NotBestAprDialog from './DSPContentView/NotBestAprDialog';

import DSPContentPlacement from './DSPContentView/ContentPlacement';
import { conformToMask, moneyMask } from 'utils/masks';
import Barcode from 'components/Barcode';
import CardDetails from './DSPContentView/CardDetails';
import Expiration from './DSPContentView/Expiration';

import pluralize from 'pluralize';

import CallToAction from './CallToAction';
import ExpandableLink from 'components/ExpandableLink/ExpandableLink';
import PageContainer from 'components/PageContainer';
import Title from 'components/Title';

interface PropTypes {
  checkoutApproval?: boolean;
  instantCredit: InstantCredit;
  tenant: Tenant;
  onSendEmailClick: () => void;
  sendingEmail: boolean;
  sendingTextToRecover: boolean;
  onTextToRecoverClick: () => void;
  onTextToRecoverFinancingClick: () => void;
}

const DSPView: React.FC<PropTypes> = props => {
  const {
    checkoutApproval,
    tenant,
    instantCredit,
    onSendEmailClick,
    sendingEmail,
    onTextToRecoverClick,
    onTextToRecoverFinancingClick,
    sendingTextToRecover,
  } = props;

  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const t = useTranslate();
  const { storeNumber } = useCachedAttribute();

  const {
    creditLimit,
    accountNumber,
    firstPurchaseLimit,
    securityCode,
    apr,
    rejectReasons = [],
    authAllowed,
    notBestAprContent,
    tenantCode,
  } = instantCredit;

  const { barcodeFormat, tspDisplay: show } = tenant;

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { isMobile } = useResponsiveQuery();

  // The presence of security code is how we distinguish a virtual card from a
  // normal card.
  const hasVirtualCard = Boolean(securityCode);

  const i18nContext = instantCredit.hasExistingAccount
    ? 'existingAccount'
    : undefined;

  return (
    <Box mt={8} mb={5}>
      <PageContainer maxWidth="sm">
        <Box mb={2}>
          <Title css={styles.pageTitleText}>
            {tMd('dsp.approvalHeading', {
              firstName: instantCredit.firstName,
              context: i18nContext,
            })}
          </Title>
        </Box>

        <Grid container spacing={2} direction="row" justify="center">
          <Grid item xs={12} md={12}>
            {notBestAprContent ? (
              <NotBestAprDialog
                notBestAprContent={notBestAprContent}
                sendingEmail={sendingEmail}
                onSendEmailClick={onSendEmailClick}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
              />
            ) : null}

            <DSPContentPlacement
              i18nKey="dsp.dspContent.placement0"
              i18nContext={i18nContext}
              tenantCode={tenantCode}
            />

            <Box alignItems="center" textAlign="center" flexDirection="column">
              <Box mb={3}>
                {!hasVirtualCard &&
                creditLimit !== null && // Check null incase api returns null value
                creditLimit !== undefined &&
                show.creditLimit ? (
                  <Fragment>
                    <p data-cy={'creditLimit'} css={styles.creditLimitText}>
                      {conformToMask(creditLimit.toString(), moneyMask)}
                    </p>
                    <p css={styles.creditLimitTitle}>
                      {tMd('dsp.dspContent.title')}
                    </p>
                  </Fragment>
                ) : null}

                {hasVirtualCard &&
                show.temporaryShoppingPassLimit &&
                firstPurchaseLimit !== null && // Check null incase api returns null value
                  firstPurchaseLimit !== undefined && (
                    <Fragment>
                      <p
                        data-cy={'temporaryShoppingPassLimit'}
                        css={styles.creditLimitText}
                      >
                        {conformToMask(
                          firstPurchaseLimit.toString(),
                          moneyMask,
                        )}
                      </p>
                      <p css={styles.creditLimitTitle}>
                        {tMd('dsp.dspContent.temporaryShoppingPassTitle')}
                      </p>
                    </Fragment>
                  )}
              </Box>

              <Box mb={3} textAlign="center">
                <DSPContentPlacement
                  i18nKey={
                    isMobile
                      ? 'dsp.dspContent.placement1Mobile'
                      : 'dsp.dspContent.placement1'
                  }
                  i18nContext={i18nContext}
                  tenantCode={tenantCode}
                />
                <div css={styles.expandableDisclosure}>
                  {t('dsp.dspContent.placement1Disclosure') ? (
                    <ExpandableLink
                      collapsedTitle={t(
                        'dsp.dspContent.placement1ShowDisclosureLabel',
                      )}
                      expandedTitle={t(
                        'dsp.dspContent.placement1HideDisclosureLabel',
                      )}
                      id="placement1Disclosure"
                      children={tMdMultiline(
                        'dsp.dspContent.placement1Disclosure',
                      )}
                    />
                  ) : null}
                </div>
              </Box>

              {isMobile && accountNumber && barcodeFormat && show.barcode ? (
                <Box data-cy={'bardode'} mb={3}>
                  <Barcode
                    format={barcodeFormat}
                    value={accountNumber}
                    displayValue={false}
                  />
                </Box>
              ) : null}

              <Box mb={1} css={styles.actionsContainer}>
                <CallToAction
                  checkoutApproval={checkoutApproval}
                  instantCredit={instantCredit}
                  tenant={tenant}
                  storeNumber={storeNumber}
                  onTextToRecoverClick={onTextToRecoverClick}
                  onTextToRecoverFinancingClick={onTextToRecoverFinancingClick}
                  sendingTextToRecover={sendingTextToRecover}
                />
              </Box>
            </Box>

            <Grid
              container
              direction="row"
              alignItems="baseline"
              spacing={1}
              css={styles.dspSection}
            >
              <CardDetails tenant={tenant} instantCredit={instantCredit} />
            </Grid>

            <div css={styles.dspSection}>
              <DSPContentPlacement
                i18nKey="dsp.dspContent.placement2"
                i18nContext={i18nContext}
                tenantCode={tenantCode}
              />

              {show.topExpireMessage ? (
                <Box data-cy={'topExpireMessage'} mt={2}>
                  <Expiration tenant={tenant} instantCredit={instantCredit} />
                </Box>
              ) : null}

              {apr &&
              parseFloat(apr) &&
              show.aprMessage &&
              !instantCredit.hasExistingAccount ? (
                <Box data-cy={'aprMessage'} mt={2}>
                  <p css={styles.normalText}>
                    {tMd('dsp.dspContent.aprMessage', { apr })}
                  </p>
                </Box>
              ) : null}

              {rejectReasons &&
              rejectReasons.length &&
              !instantCredit.hasExistingAccount ? (
                <Box mt={2}>
                  <p css={styles.normalText}>
                    {tMd('dsp.dspContent.notBestAprMessage')}
                    &#160;
                    <button
                      onClick={() => setModalOpen(true)}
                      css={styles.link}
                    >
                      {tMd('dsp.dspContent.notBestAprLink')}
                    </button>
                  </p>
                </Box>
              ) : null}

              {hasVirtualCard &&
              firstPurchaseLimit !== null && // Check null incase api returns null value
                firstPurchaseLimit !== undefined &&
                authAllowed &&
                show.transactionLimitMessage && (
                  <Box data-cy={'transactionLimitMessage'} mt={2}>
                    <p css={styles.normalText}>
                      {tMd('dsp.dspContent.transactionLimitMessage', {
                        transactions: pluralize(
                          'transaction',
                          authAllowed,
                          true,
                        ),
                        limit: conformToMask(
                          firstPurchaseLimit.toString(),
                          moneyMask,
                        ),
                      })}
                    </p>
                  </Box>
                )}

              {hasVirtualCard &&
              creditLimit !== null && // Check null incase api returns null value
                creditLimit !== undefined && (
                  <Box mt={2}>
                    <p css={styles.normalText}>
                      <strong>
                        {tMd('dsp.dspContent.creditLimitMessage', {
                          context: i18nContext,
                          limit: conformToMask(
                            creditLimit.toString(),
                            moneyMask,
                          ),
                        })}
                      </strong>
                    </p>
                  </Box>
                )}

              <DSPContentPlacement
                i18nKey="dsp.dspContent.placement3"
                i18nContext={i18nContext}
                tenantCode={tenantCode}
              />
            </div>
          </Grid>
        </Grid>
      </PageContainer>
    </Box>
  );
};

export default DSPView;
